/**
 * avatar-image.ts — client-side avatar image handling for the EinstellungenScreen
 * upload flow (Part C).
 *
 * Replaces the old "paste an avatar URL" input: the user picks a local file, it is
 * validated (type + size, BEFORE any processing) and downscaled in the browser to a
 * square ~256x256 JPEG, then uploaded as bytes (never a URL — no SSRF surface).
 *
 * The server (ProfileService.updateAvatar) re-validates everything from the actual
 * bytes regardless (rule #2 — no business logic trusted from the client); this
 * module's checks are a UX nicety, not the source of truth.
 *
 * [Source: apps/api/src/modules/me/avatar-image.util.ts] (server-side mirror)
 */

/** Accepted source types the user may pick — mirrors AVATAR_ALLOWED_MIME on the api. */
export const AVATAR_ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;

/** Reject the ORIGINAL picked file above this size — before any downscaling is attempted. */
export const AVATAR_MAX_SOURCE_BYTES = 2 * 1024 * 1024;

/** The square side length + JPEG quality the picked image is downscaled to. */
export const AVATAR_TARGET_SIZE = 256;
const AVATAR_JPEG_QUALITY = 0.85;

export type AvatarValidationError = 'invalid_type' | 'too_large';

/** Pure: validate a picked file BEFORE attempting to downscale it. */
export function validateAvatarFile(file: { type: string; size: number }): AvatarValidationError | null {
  if (!(AVATAR_ACCEPTED_TYPES as readonly string[]).includes(file.type)) return 'invalid_type';
  if (file.size > AVATAR_MAX_SOURCE_BYTES) return 'too_large';
  return null;
}

/**
 * Downscale a picked image file to a square JPEG blob: center-crop to a square, then
 * scale to `size`x`size`. Uses createImageBitmap when available (no <img> round-trip),
 * falling back to an off-DOM Image element otherwise.
 */
export async function resizeAvatarToSquareJpeg(
  file: File,
  size: number = AVATAR_TARGET_SIZE,
  quality: number = AVATAR_JPEG_QUALITY,
): Promise<Blob> {
  const source = await loadDrawableImage(file);
  const { width, height } = sourceDimensions(source);

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('canvas_unsupported');
  }

  // Center-crop to a square from the source, then scale into the target canvas.
  const cropSize = Math.min(width, height);
  const sx = (width - cropSize) / 2;
  const sy = (height - cropSize) / 2;
  ctx.drawImage(source, sx, sy, cropSize, cropSize, 0, 0, size, size);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('canvas_export_failed'))),
      'image/jpeg',
      quality,
    );
  });
}

/** Read a Blob into a `data:` URI (used for the mock-mode preview, no network). */
export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('read_failed'));
    reader.readAsDataURL(blob);
  });
}

type DrawableImage = ImageBitmap | HTMLImageElement;

function sourceDimensions(source: DrawableImage): { width: number; height: number } {
  if (source instanceof HTMLImageElement) {
    return { width: source.naturalWidth, height: source.naturalHeight };
  }
  return { width: source.width, height: source.height };
}

async function loadDrawableImage(file: File): Promise<DrawableImage> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file);
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('image_load_failed'));
      img.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}
